import type {Theme, ThemeSize, WithThemeProp} from '../theme';

import {getInByToken} from '../../utils/object';
import {ms, msi} from '../normalizeSize';

type Normalizer = typeof ms | typeof msi;

const valueOrNormalized = (
  value: number,
  normalize: boolean,
  normalizer: Normalizer,
) => (normalize ? normalizer(value) : value);

const getSize =
  (normalizer: Normalizer, size?: number | ThemeSize, normalize = true) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    switch (typeof size) {
      case 'number':
        return valueOrNormalized(size, normalize, normalizer);
      case 'undefined':
        //eslint-disable-next-line no-console
        console.warn('getThemeSize: size is undefined');

        return size;
      default: {
        const result = getInByToken<Theme['size']>(p.theme.size, size);

        return valueOrNormalized(result, normalize, normalizer);
      }
    }
  };

/**
 * Utility to get size from the theme by token
 * inside the `css` or `styled` template literal.
 * Automatically add 'px' unit.
 *
 * @param size - `size token` or `number`
 * @param normalize - should normalize `size` or return `size` as is
 *
 * @returns `size` as `string` with `px` unit
 *
 * @example
 * let size = 'gap.me'
 * css`
 *  width: ${getThemeSize(size)}; => theme.size.gap.me + px;
 * `
 *
 * let size = 10
 * css`
 *  width: ${getThemeSize(size)}; => 10px;
 * `
 */
export const getThemeSize = (size?: number | ThemeSize, normalize = true) =>
  getSize(ms, size, normalize);

/**
 * Utility to get size from the theme by token
 * inside the `css` or `styled` template literal.
 * Always returns `number` .
 *
 * @param size - `size token` or `number`
 * @param normalize - should normalize `size` or return `size` as is
 *
 * @returns `size` as `number`
 *
 * @example
 * let size = 'gap.me'
 * css`
 *  width: ${getThemeSize(size)}; => theme.size.gap.me;
 * `
 *
 * let size = 10
 * css`
 *  width: ${getThemeSize(size)}; => 10;
 * `
 */
export const getThemeSizeInt = (size?: number | ThemeSize, normalize = true) =>
  getSize(msi, size, normalize);

// shorthands

/**
 * Shortcut for {@link getThemeSize}
 * @alias
 */
export const gts = getThemeSize;

/**
 * Shortcut for {@link getThemeSizeInt}
 * @alias
 */
export const gtsi = getThemeSizeInt;
